import React, { useEffect, useState } from 'react';
import { Card, Table, Badge, Button, Spinner, Container, Row, Col, Alert } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { fetchCampaignDetails } from '../../services/api';

const CampaignDetails = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        const response = await fetchCampaignDetails(id);
        if (response.success) {
            setData(response);
            setLastUpdate(new Date());
        }
        setLoading(false);
    };

    const copyLink = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('Link copiado!');
        } catch (err) {
            console.error(err);
        }
    };

    if (loading && !data) return <div className="text-center py-5"><Spinner animation="border" /></div>;
    if (!data) return <Alert variant="danger">Campanha não encontrada.</Alert>;

    const { campanha, dashboard, clientes, tipo } = data;
    const isWhatsApp = tipo === 'whatsapp';

    return (
        <Container fluid className="p-4 fade-in">
            <div className="mb-4 d-flex justify-content-between align-items-start">
                <div>
                    <Link to="/dashboard/campanhas" className="text-decoration-none">← Voltar para Campanhas</Link>
                    <h2 className="mt-2 mb-0 text-primary">
                        {isWhatsApp && <Badge bg="success" className="me-2 fs-6">📱 WhatsApp</Badge>}
                        {!isWhatsApp && <Badge bg="secondary" className="me-2 fs-6">🔗 Link</Badge>}
                        {campanha.nome}
                    </h2>
                    <div className="text-muted">{campanha.descricao}</div>
                    <div className="text-muted small">Criada em {campanha.data_criacao ? new Date(campanha.data_criacao).toLocaleString('pt-BR') : '—'}</div>
                </div>
                <div className="text-end">
                    <Button variant="outline-primary" onClick={loadData} disabled={loading} className="mb-2">
                        {loading ? (<><Spinner animation="border" size="sm" className="me-2" />Atualizando...</>) : (<>🔄 Atualizar Dados</>)}
                    </Button>
                    {lastUpdate && (
                        <div className="text-muted small">
                            Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
                        </div>
                    )}
                </div>
            </div>

            {/* Dashboard KPIs */}
            {isWhatsApp ? (
                /* ── WhatsApp Dashboard ── */
                <Row className="g-3 mb-4">
                    <Col md={2}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">Total Clientes</h6>
                                <h3 className="text-dark fw-bold mb-0">{dashboard.totalClientes}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">✉️ Enviados</h6>
                                <h3 className="text-primary fw-bold mb-0">{dashboard.totalEnviados}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">✅ Entregues</h6>
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                    <h3 className="text-success fw-bold mb-0">{dashboard.totalEntregues}</h3>
                                    <Badge bg="success">{dashboard.taxaEntrega}%</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">👁️ Lidos</h6>
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                    <h3 className="text-info fw-bold mb-0">{dashboard.totalLidos}</h3>
                                    <Badge bg="info">{dashboard.taxaLeitura}%</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={2}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">❌ Falhas</h6>
                                <h3 className="text-danger fw-bold mb-0">{dashboard.totalFalhas}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            ) : (
                /* ── CRM Dashboard ── */
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">Total Clientes</h6>
                                <h3 className="text-dark fw-bold mb-0">{dashboard.totalClientes}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">Clicaram no Link</h6>
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                    <h3 className="text-primary fw-bold mb-0">{dashboard.totalClicaram}</h3>
                                    <Badge bg="primary">{dashboard.taxaAbertura}%</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm text-center h-100">
                            <Card.Body>
                                <h6 className="text-muted mb-2">Compraram</h6>
                                <div className="d-flex justify-content-center align-items-center gap-2">
                                    <h3 className="text-success fw-bold mb-0">{dashboard.totalCompraram}</h3>
                                    <Badge bg="success">{dashboard.taxaConversao}%</Badge>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm text-center h-100 bg-success bg-opacity-10">
                            <Card.Body>
                                <h6 className="text-success mb-2">Receita Gerada</h6>
                                <h3 className="text-success fw-bold mb-0">
                                    R$ {(dashboard.receitaTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Lista de Clientes */}
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0">{isWhatsApp ? 'Destinatários da Campanha' : 'Lista de Clientes e Links'}</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    {isWhatsApp ? (
                        /* ── WhatsApp Recipients Table ── */
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-3">Contato</th>
                                    <th>Telefone</th>
                                    <th className="text-center">Status</th>
                                    <th>Enviado em</th>
                                    <th>Erro</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientes.map((c, idx) => (
                                    <tr key={idx}>
                                        <td className="ps-3">
                                            <div className="fw-bold">{c.contato_nome || 'Desconhecido'}</div>
                                        </td>
                                        <td>
                                            <span className="text-muted">{c.telefone || '—'}</span>
                                        </td>
                                        <td className="text-center">
                                            <Badge bg={
                                                c.status === 'read' ? 'info' :
                                                    c.status === 'delivered' ? 'success' :
                                                        c.status === 'sent' ? 'primary' :
                                                            c.status === 'failed' ? 'danger' :
                                                                'secondary'
                                            }>
                                                {c.status === 'read' ? '👁️ Lido' :
                                                    c.status === 'delivered' ? '✅ Entregue' :
                                                        c.status === 'sent' ? '✉️ Enviado' :
                                                            c.status === 'failed' ? '❌ Falhou' :
                                                                c.status === 'pending' ? '⏳ Pendente' : c.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            <span className="text-muted small">
                                                {c.sent_at ? new Date(c.sent_at).toLocaleString('pt-BR') : '—'}
                                            </span>
                                        </td>
                                        <td>
                                            {c.last_error && (
                                                <span className="text-danger small">{c.last_error}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        /* ── CRM Clients Table ── */
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-3">Cliente</th>
                                    <th>Contato</th>
                                    <th>Link Rastreável</th>
                                    <th className="text-center">Acessos</th>
                                    <th className="text-center">Status Venda</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientes.map((c, idx) => (
                                    <tr key={idx} className={c.comprou ? 'table-success' : ''}>
                                        <td className="ps-3">
                                            <div className="fw-bold">{c.contato_nome || 'Desconhecido'}</div>
                                        </td>
                                        <td>
                                            <div className="small text-muted">
                                                {c.email && <div>📧 {c.email}</div>}
                                                {(c.celular || c.telefone) && <div>📱 {c.celular || c.telefone}</div>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="input-group input-group-sm" style={{ maxWidth: '300px' }}>
                                                <input type="text" className="form-control" value={c.link_rastreavel} readOnly />
                                                <button className="btn btn-outline-secondary" onClick={() => copyLink(c.link_rastreavel)}>
                                                    🔗
                                                </button>
                                                <a
                                                    href={`https://wa.me/55${(c.celular || c.telefone || '').replace(/\D/g, '')}?text=${encodeURIComponent(`Olá ${c.contato_nome?.split(' ')[0]}, confira esta oferta especial: ${c.link_rastreavel}`)}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={`btn btn-outline-success ${!(c.celular || c.telefone) ? 'disabled' : ''}`}
                                                >
                                                    📱
                                                </a>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {c.cliques > 0 ? (
                                                <Badge bg="primary">{c.cliques} acessos</Badge>
                                            ) : (
                                                <span className="text-muted small">-</span>
                                            )}
                                            {c.ultimo_acesso && (
                                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                    Último: {new Date(c.ultimo_acesso).toLocaleDateString()}
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {c.comprou ? (
                                                <div>
                                                    <Badge bg="success" className="mb-1">COMPROU</Badge>
                                                    <div className="fw-bold text-success small">
                                                        R$ {(c.valor_recuperado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <Badge bg="light" text="dark" className="border">Pendente</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CampaignDetails;

